import React, { useState, useEffect } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Card, CardContent, CardHeader } from "../../ui/card";
import {
  MagnifyingGlass,
  ArrowCircleRight,
  User,
  CalendarBlank,
  CaretLeft,
  CaretRight,
  House,
  ArrowLeft,
} from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useGetInternsForSupervisor } from "../../../adapters/Requests";
import TopNav from "../../Navbars/TopNav";
import Sidebar from "../../Navbars/sidebar";

export default function MonthlyAssesmentTable() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { data, isLoading } = useGetInternsForSupervisor();
  const navigate = useNavigate();

  const [globalFilter, setGlobalFilter] = useState("");
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  useEffect(() => {
    if (data && Array.isArray(data)) {
      const formattedData = data.map((intern) => ({
        id: intern.id,
        firstName: `${intern.Applicant.firstname} ${intern.Applicant.lastname}`,
        status: intern.status || "unknown",
        age:
          new Date().getFullYear() -
          new Date(intern.Applicant.date_of_birth).getFullYear(),
        cos: "Computer Science",
        date: new Date(intern.created_at).toLocaleDateString(),
        original: intern,
      }));
      setTableData(formattedData);
    }
  }, [data]);

  const handleArrowClick = async (applications_id, status) => {
    try {
      localStorage.setItem("applications_id", applications_id);
      localStorage.setItem("status", status);
      setTimeout(() => {
        localStorage.removeItem("applications_id");
        localStorage.removeItem("status");
      }, 5 * 60 * 1000);
    } catch (error) {
      console.error("Error storing application ID:", error);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "verified":
        return "success";
      case "in-review":
        return "warning";
      default:
        return "secondary";
    }
  };

  const columns = [
    {
      accessorKey: "firstName",
      header: "Intern Name",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center text-green-700">
            <User size={16} weight="bold" />
          </div>
          <span className="font-medium text-sm text-gray-900">
            {row.getValue("firstName")}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "cos",
      header: "Department",
      cell: ({ row }) => (
        <span className="text-gray-600 text-sm">{row.getValue("cos")}</span>
      ),
    },
    {
      accessorKey: "date",
      header: "Date Added",
      cell: ({ row }) => (
        <div className="flex items-center text-gray-500 text-sm">
          <CalendarBlank className="mr-1.5" size={14} />
          {row.getValue("date")}
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <Badge
          variant={getStatusColor(row.getValue("status"))}
          className="text-[10px] uppercase"
        >
          {row.getValue("status")}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <Link
          to={`/monthlyassesment/${row.original.id}`}
          onClick={() => handleArrowClick(row.original.id, row.original.status)}
        >
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
          >
            <ArrowCircleRight size={24} weight="fill" />
          </Button>
        </Link>
      ),
    },
  ];

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    state: {
      globalFilter,
      pagination,
    },
  });

  return (
    <>
      <TopNav />
      <div className="flex h-screen overflow-hidden bg-gray-50">
        <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden pt-20 px-4 sm:px-6 lg:px-8 pb-8">
          <div className="flex flex-col gap-6 max-w-5xl mx-auto w-full">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Link
                  to="/dashboard"
                  className="flex items-center hover:text-green-600 transition-colors"
                >
                  <House size={16} />
                </Link>
                <CaretRight size={14} className="text-gray-300" />
                <span className="font-semibold text-gray-900">
                  Monthly Assessments
                </span>
              </div>

              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-900 bg-white border border-gray-200 px-3 py-1.5 rounded-full shadow-sm transition-colors"
              >
                <ArrowLeft size={16} /> Back
              </button>
            </div>

            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                Pending Assessments
              </h1>
            </div>

            <Card className="w-full border-slate-200 shadow-sm bg-white">
              <CardHeader className="p-4 border-b border-slate-100 bg-white">
                <div className="relative w-full sm:w-72">
                  <MagnifyingGlass
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    size={14}
                  />
                  <Input
                    placeholder="Search interns..."
                    value={globalFilter ?? ""}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    className="pl-9 h-9 text-sm bg-gray-50 border-slate-200 focus:bg-white transition-colors"
                  />
                </div>
              </CardHeader>

              <CardContent className="p-0">
                <Table>
                  <TableHeader className="bg-gray-50/50">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow
                        key={headerGroup.id}
                        className="hover:bg-transparent border-b border-slate-100"
                      >
                        {headerGroup.headers.map((header) => (
                          <TableHead
                            key={header.id}
                            className="h-10 text-xs font-semibold text-gray-500 uppercase tracking-wider"
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-48 text-center"
                        >
                          <div className="flex justify-center items-center w-full h-full">
                            <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : table.getRowModel().rows?.length ? (
                      table.getRowModel().rows.map((row) => (
                        <TableRow
                          key={row.id}
                          className="hover:bg-slate-50/60 transition-colors border-b border-slate-100"
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id} className="py-3">
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-32 text-center text-gray-500"
                        >
                          No interns found for assessment.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>

              <div className="flex items-center justify-between p-3 border-t border-slate-100 bg-gray-50/30">
                <div className="text-xs text-gray-500">
                  Page {table.getState().pagination.pageIndex + 1} of{" "}
                  {table.getPageCount()}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    className="h-7 w-20 bg-white text-xs"
                  >
                    <CaretLeft className="mr-1" size={12} /> Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                    className="h-7 w-20 bg-white text-xs"
                  >
                    Next <CaretRight className="ml-1" size={12} />
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
